import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@/components/ui/responsive-modal';
import { Booking } from '@/types/Booking';
import { Service } from '@/types/Service';
import { Contact } from '@/types/Contact';
import { parseISO } from 'date-fns';
import { ExternalLink, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDateFormat } from '@/hooks/useDateFormat';

interface AppointmentDetailsPanelProps {
  booking: Booking;
  service?: Service | null;
  contact?: Contact | null;
  onEdit: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onClose?: () => void;
  showCloseButton?: boolean;
  compact?: boolean;
  readOnly?: boolean;
}

export const AppointmentDetailsPanel: React.FC<
  AppointmentDetailsPanelProps
> = ({
  booking,
  service,
  contact,
  onEdit,
  onCancel,
  onDelete,
  onClose,
  showCloseButton = false,
  compact = false,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const df = useDateFormat();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isCancelled = booking.status.toLowerCase() === 'cancelled';

  const startTime = parseISO(booking.start_time);
  const endTime = parseISO(booking.end_time);
  const dateStr = df.dateLong(startTime);
  const timeRange = `${df.time(startTime)} - ${df.time(endTime)}`;

  const getStatusBadgeClasses = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'border-transparent bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800';
      case 'pending':
        return 'border-transparent bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-200 dark:hover:bg-yellow-800';
      case 'cancelled':
        return 'border-transparent bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800';
      default:
        return 'border-transparent bg-muted text-muted-foreground hover:bg-muted/80';
    }
  };

  const destructiveOutlineClass =
    'border-destructive text-destructive bg-background hover:bg-destructive/10 hover:text-destructive';

  return (
    <>
      <div className="relative w-full">
        {showCloseButton && onClose && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute right-0 top-0 z-10 h-8 w-8 text-muted-foreground hover:text-foreground"
            aria-label={t('common.close.translation', 'Close')}
          >
            <X className="h-4 w-4" />
          </Button>
        )}

        <div className={cn('mb-4', showCloseButton && 'pr-9')}>
          <h3 className="text-lg font-semibold text-foreground">
            {t(
              'agent.calendar.appointment.details.title.translation',
              'Appointment Details'
            )}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span>{dateStr}</span>
            <Badge
              variant="outline"
              className={cn('text-xs', getStatusBadgeClasses(booking.status))}
            >
              {booking.status}
            </Badge>
          </div>
        </div>

        <div className="space-y-4">
          <div
            className={cn('gap-4', compact ? 'space-y-4' : 'grid grid-cols-2')}
          >
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">
                {t('agent.calendar.appointment.service.translation', 'Service')}
              </p>
              <p className="text-sm text-foreground">
                {service?.name ||
                  t(
                    'agent.calendar.appointment.no_service.translation',
                    'No Service'
                  )}
              </p>
              {service?.description && (
                <p className="text-xs text-muted-foreground">
                  {service.description}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">
                {t('agent.calendar.appointment.contact.translation', 'Contact')}
              </p>
              {contact ? (
                <div>
                  <p className="text-sm text-foreground">
                    {contact.name || contact.caller_id}
                  </p>
                  {contact.name && (
                    <p className="text-xs text-muted-foreground">
                      {contact.caller_id}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">—</p>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">
              {t('agent.calendar.appointment.time.translation', 'Time')}
            </p>
            <p className="text-sm text-foreground">{timeRange}</p>
          </div>

          {booking.conversation_id && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">
                {t(
                  'agent.calendar.appointment.conversation.translation',
                  'Conversation'
                )}
              </p>
              <Link
                to={`/agents/${booking.agent_id}/conversation/${booking.conversation_id}`}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                {t(
                  'agent.calendar.appointment.view_conversation.translation',
                  'View conversation'
                )}
                <ExternalLink className="h-3.5 w-3.5" />
              </Link>
            </div>
          )}

          {booking.customer_notes && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">
                {t(
                  'agent.calendar.appointment.customer_notes.translation',
                  'Customer Notes'
                )}
              </p>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {booking.customer_notes}
              </p>
            </div>
          )}
        </div>

        {onClose && !showCloseButton && (
          <Button
            variant="transparent"
            size="sm"
            onClick={onClose}
            className="mt-4 shrink-0 px-3"
          >
            {t('common.close.translation', 'Close')}
          </Button>
        )}

        {!readOnly && (
          <div className="mt-6 grid w-full min-w-0 grid-cols-2 gap-2">
            {isCancelled ? (
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(true)}
                className={cn('w-full min-w-0', destructiveOutlineClass)}
              >
                {t('common.delete.translation', 'Delete')}
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => setShowCancelConfirm(true)}
                className={cn('w-full min-w-0', destructiveOutlineClass)}
              >
                {t(
                  'agent.calendar.appointment.cancel.translation',
                  'Cancel Appointment'
                )}
              </Button>
            )}
            <Button variant="brand" onClick={onEdit} className="w-full min-w-0">
              {t('common.edit.translation', 'Edit')}
            </Button>
          </div>
        )}
      </div>

      <Modal open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <ModalContent className="w-full max-w-lg">
          <ModalHeader>
            <ModalTitle
              description={t(
                'agent.calendar.appointment.cancel_confirm.description.translation',
                'Are you sure you want to cancel this appointment? You can delete it permanently after cancellation.'
              )}
            >
              {t(
                'agent.calendar.appointment.cancel_confirm.title.translation',
                'Cancel Appointment'
              )}
            </ModalTitle>
          </ModalHeader>
          <ModalFooter>
            <Button
              variant="outline"
              onClick={() => setShowCancelConfirm(false)}
            >
              {t('common.cancel.translation', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowCancelConfirm(false);
                onCancel();
              }}
            >
              {t(
                'agent.calendar.appointment.cancel.translation',
                'Cancel Appointment'
              )}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <ModalContent className="w-full max-w-lg">
          <ModalHeader>
            <ModalTitle
              description={t(
                'agent.calendar.appointment.delete_confirm.description.translation',
                'Are you sure you want to permanently delete this cancelled appointment? This action cannot be undone.'
              )}
            >
              {t(
                'agent.calendar.appointment.delete_confirm.title.translation',
                'Delete Appointment'
              )}
            </ModalTitle>
          </ModalHeader>
          <ModalFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
            >
              {t('common.cancel.translation', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowDeleteConfirm(false);
                onDelete();
              }}
            >
              {t('common.delete.translation', 'Delete')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
